import { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { Filter, X, ChevronDown, ChevronUp } from 'lucide-react';
import SearchableSelect from '../ui/SearchableSelect';
import { cn } from '../../lib/utils';
import { coreAPI, purchasingAPI, customersAPI, Site, Company, CompanyGroup, Warehouse, CustomerRatingThreshold, CommodityGroup, CommodityType } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
// CR-4783 follow-up: share the dashboard's Quick Date Ranges helper so the
// two screens compute the same dateFrom/dateTo for a given preset.
import { getLast12MonthsRange } from '../dashboard/DashboardFilters';

export interface CustomerPerformanceFilterValues {
  division: string;
  site: string;
  customerGroup: string;
  customerSite: string;
  customerCode: string;
  warehouse: string;
  strategic: boolean;
  hideChildCompanies: boolean;
  commodityGroup: string;
  commodityType: string;
  timePeriod: string;
  dateFrom: string; // YYYY-MM-DD format
  dateTo: string; // YYYY-MM-DD format
  orderType: string[];
  customerType: string;
  rating: string;
  excludeNARatings: boolean;
  dataMode: 'historical' | 'live';
  // CR-4783: Geographic filters
  siteCountry: string;
  customerCountry: string;
}

const DEFAULT_FILTER_VALUES: CustomerPerformanceFilterValues = {
  division: 'all',
  site: 'all',
  customerGroup: 'all',
  customerSite: 'all',
  customerCode: '',
  warehouse: 'all',
  strategic: false,
  hideChildCompanies: false,
  commodityGroup: 'all',
  commodityType: 'all',
  timePeriod: 'all',
  dateFrom: '',
  dateTo: '',
  orderType: [],
  customerType: 'all',
  rating: 'all',
  excludeNARatings: true,
  dataMode: 'historical',
  siteCountry: 'all',
  customerCountry: 'all',
};

interface CustomerPerformanceFiltersProps {
  filters: CustomerPerformanceFilterValues;
  onApplyFilters: (filters: CustomerPerformanceFilterValues) => void;
  ratingThresholds: CustomerRatingThreshold[];
}

function formatLocalDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

type QuickRangeKey = 'last12Months' | 'thisMonth' | 'lastMonth' | 'lastQuarter' | 'thisYear' | 'lastYear';

function datesForQuickRange(range: QuickRangeKey): { dateFrom: string; dateTo: string } {
  const today = new Date();
  switch (range) {
    case 'last12Months':
      return getLast12MonthsRange();
    case 'thisMonth':
      return {
        dateFrom: formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
      };
    case 'lastMonth':
      return {
        dateFrom: formatLocalDate(new Date(today.getFullYear(), today.getMonth() - 1, 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth(), 0)),
      };
    case 'lastQuarter': {
      const lq = Math.floor(today.getMonth() / 3) - 1;
      const lqy = lq < 0 ? today.getFullYear() - 1 : today.getFullYear();
      const aq = lq < 0 ? 3 : lq;
      return {
        dateFrom: formatLocalDate(new Date(lqy, aq * 3, 1)),
        dateTo: formatLocalDate(new Date(lqy, (aq + 1) * 3, 0)),
      };
    }
    case 'thisYear':
      return {
        dateFrom: formatLocalDate(new Date(today.getFullYear(), 0, 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear(), today.getMonth() + 1, 0)),
      };
    case 'lastYear':
      return {
        dateFrom: formatLocalDate(new Date(today.getFullYear() - 1, 0, 1)),
        dateTo: formatLocalDate(new Date(today.getFullYear() - 1, 11, 31)),
      };
  }
}

const ORDER_TYPES = [
  { value: 'standard', label: 'Standard' },
  { value: 'blanket', label: 'Blanket' },
  { value: 'contract', label: 'Contract' },
  { value: 'spot', label: 'Spot Buy' },
  { value: 'consignment', label: 'Consignment' },
];

// CR-4783: Customer Type now segments by organisational relationship
// (same group vs different group relative to the viewing supplier).
const CUSTOMER_TYPES = [
  { value: 'all', label: 'All' },
  { value: 'internal', label: 'Internal' },
  { value: 'external', label: 'External' },
];

/** Extract array from either a flat array response or paginated {results} response */
function extractResults<T>(data: unknown): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && 'results' in (data as Record<string, unknown>)) {
    return ((data as Record<string, unknown>)['results'] as T[]) || [];
  }
  return [];
}

export default function CustomerPerformanceFilters({
  filters,
  onApplyFilters,
  ratingThresholds,
}: CustomerPerformanceFiltersProps) {
  const { user } = useAuth();
  const [divisions, setDivisions] = useState<Company[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [customerSites, setCustomerSites] = useState<Site[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [customerGroups, setCustomerGroups] = useState<CompanyGroup[]>([]);
  const [commodityGroups, setCommodityGroups] = useState<CommodityGroup[]>([]);
  const [commodityTypes, setCommodityTypes] = useState<CommodityType[]>([]);
  const [_loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false); // Start collapsed for cleaner UX

  // Local pending filter state — only synced to parent when Apply or Clear is clicked
  const [localFilters, setLocalFilters] = useState<CustomerPerformanceFilterValues>(filters);

  // Sync local state if parent resets filters externally (e.g. from Clear All)
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  // Fetch filter options on mount
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        setLoading(true);
        const isHelixUser = user?.platform === 'HELIX';

        // Fetch commodity data (available to all users)
        const commodityPromises = [
          purchasingAPI.getCommodityGroups({ enabled: true, page_size: 100 }).catch(() => ({ data: [] })),
          purchasingAPI.getCommodityTypes({ enabled: true, page_size: 200 }).catch(() => ({ data: [] })),
        ] as const;

        if (isHelixUser) {
          // Helix users can access all data
          const [supplierRes, customerRes, groupsResponse, warehousesResponse, companiesResponse, commodityGroupsResponse, commodityTypesResponse] = await Promise.all([
            coreAPI.getCompanies({ company_type: 'supplier', is_active: true }),
            coreAPI.getCompanies({ company_type: 'customer', is_active: true, page_size: 1000 }),
            coreAPI.getCompanyGroups({ is_active: true, company_type: 'customer' }),
            coreAPI.getWarehouses({ is_active: true }),
            // Divisions dropdown is the customer directory — request it
            // explicitly now that companies/accessible/ defaults to the
            // operating org's own sites rather than the partner directory.
            coreAPI.getAccessibleCompanies({ type: 'customer' }).catch(() => ({ data: { results: [] } })),
            ...commodityPromises,
          ]);
          const supplierCompanies = supplierRes.data.results || [];
          const customerCompanies = customerRes.data.results || [];
          setSites(supplierCompanies as unknown as Site[]);
          setCustomerSites(customerCompanies as unknown as Site[]);
          setCustomerGroups(groupsResponse.data.results || []);
          setWarehouses(warehousesResponse.data.results || []);
          setCommodityGroups(extractResults(commodityGroupsResponse.data));
          setCommodityTypes(extractResults(commodityTypesResponse.data));
          const companies = companiesResponse.data.results || [];
          setDivisions(companies.length > 1 ? companies : []);
        } else {
          // Non-Helix users use accessible endpoints only.
          // CR-4887: customer-portal users want SUPPLIER hierarchy in the
          // Company Site / Division dropdowns. The Customer Performance
          // page mirrors the Dashboard's filter panel; flip the accessible
          // endpoints with ?context=supplier_filter for the same reason.
          const isCustomerPortal = user?.platform === 'CUSTOMER';
          const filterContext = isCustomerPortal ? { context: 'supplier_filter' as const } : undefined;
          const [sitesResponse, groupsResponse, companiesResponse, customersResponse, commodityGroupsResponse, commodityTypesResponse] = await Promise.all([
            coreAPI.getAccessibleSites(filterContext).catch(() => ({ data: [] })),
            coreAPI.getCustomerGroupsForFilter(filterContext).catch(() => ({ data: { results: [] } })),
            coreAPI.getAccessibleCompanies(filterContext).catch(() => ({ data: { results: [] } })),
            customersAPI.getProfiles({ page_size: 1000, minimal: true }).catch(() => ({ data: { results: [] } })),
            ...commodityPromises,
          ]);
          const accessibleSites = Array.isArray(sitesResponse.data) ? sitesResponse.data : [];
          setSites(accessibleSites);
          const customerCompanies = (customersResponse.data.results || []).map(s => ({
            id: s.company_id,
            name: s.company_name,
          })) as unknown as Site[];
          setCustomerSites(customerCompanies);
          setCustomerGroups(groupsResponse.data.results || []);
          setWarehouses([]); // Warehouses not available for non-Helix users
          setCommodityGroups(extractResults(commodityGroupsResponse.data));
          setCommodityTypes(extractResults(commodityTypesResponse.data));
          const companies = companiesResponse.data.results || [];
          setDivisions(companies.length > 1 ? companies : []);
        }
      } catch (error) {
        // Silently handle - filters will just be empty
      } finally {
        setLoading(false);
      }
    };
    fetchFilterOptions();
  }, [user?.platform]);

  const updateFilter = <K extends keyof CustomerPerformanceFilterValues>(
    key: K,
    value: CustomerPerformanceFilterValues[K]
  ) => {
    setLocalFilters(prev => ({ ...prev, [key]: value }));
  };

  // CR-4783 follow-up: apply a Quick Date Range preset (mirrors the
  // dashboard's button row — same labels, same date math).
  const applyQuickRange = (range: QuickRangeKey) => {
    const { dateFrom, dateTo } = datesForQuickRange(range);
    setLocalFilters(prev => ({ ...prev, dateFrom, dateTo, timePeriod: range }));
  };

  const activeQuickRange: QuickRangeKey | null = (
    ['last12Months', 'thisMonth', 'lastMonth', 'lastQuarter', 'thisYear', 'lastYear'] as QuickRangeKey[]
  ).find((range) => {
    const d = datesForQuickRange(range);
    return d.dateFrom === localFilters.dateFrom && d.dateTo === localFilters.dateTo;
  }) ?? null;

  const toggleOrderType = (orderType: string) => {
    setLocalFilters(prev => {
      const currentTypes = prev.orderType;
      return {
        ...prev,
        orderType: currentTypes.includes(orderType)
          ? currentTypes.filter(t => t !== orderType)
          : [...currentTypes, orderType],
      };
    });
  };

  const clearFilters = () => {
    setLocalFilters(DEFAULT_FILTER_VALUES);
    onApplyFilters(DEFAULT_FILTER_VALUES);
  };

  const activeFilterCount = Object.entries(localFilters).filter(([key, value]) => {
    if (key === 'excludeNARatings') return !value; // Count if unchecked (default is true)
    if (key === 'strategic' || key === 'hideChildCompanies') return value === true;
    if (key === 'orderType') return (value as string[]).length > 0;
    if (key === 'dateFrom' || key === 'dateTo') return value !== '';
    if (key === 'dataMode') return value !== 'historical';
    return value !== 'all' && value !== '';
  }).length;

  return (
    <Card className={cn("mb-4", !expanded && "bg-muted/30")}>
      <CardContent className={cn("p-4", !expanded && "pb-4")}>
        {/* Filter Header - Always visible */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          >
            <div className={cn(
              "p-2 rounded-lg",
              expanded ? "bg-primary text-primary-foreground" : "bg-muted"
            )}>
              <Filter className="h-5 w-5" />
            </div>
            <div className="text-left">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold">Filters</h3>
                {activeFilterCount > 0 && (
                  <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                    {activeFilterCount} active
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {expanded ? 'Click to collapse' : 'Click to expand filter options'}
              </p>
            </div>
            <div className={cn(
              "ml-2 p-1 rounded transition-colors",
              expanded ? "bg-primary/10" : "bg-muted"
            )}>
              {expanded ? (
                <ChevronUp className="h-5 w-5 text-primary" />
              ) : (
                <ChevronDown className="h-5 w-5 text-muted-foreground" />
              )}
            </div>
          </button>

          {/* Action buttons - always visible */}
          <div className="flex items-center gap-2">
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-muted-foreground"
              >
                <X className="h-4 w-4 mr-1" />
                Clear All
              </Button>
            )}
          </div>
        </div>

        {expanded && (
          <>
            {/* CR-4783: Quick Date Ranges — same button row used on the
                main Dashboard so users see a consistent control across
                screens. Selecting a preset populates dateFrom/dateTo. */}
            <div className="mb-6">
              <label className="text-sm font-semibold mb-3 block text-foreground">Quick Date Ranges</label>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => applyQuickRange('last12Months')}
                  className={`px-4 py-2 text-sm font-medium border-2 rounded-md transition-colors ${
                    activeQuickRange === 'last12Months'
                      ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                      : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary'
                  }`}
                >
                  Last 12 Months (Default)
                </button>
                {(['thisMonth', 'lastMonth', 'lastQuarter', 'thisYear', 'lastYear'] as QuickRangeKey[]).map((range) => {
                  const isActive = activeQuickRange === range;
                  const label = range === 'thisMonth'
                    ? 'This Month (Live)'
                    : range.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());
                  return (
                    <button
                      key={range}
                      type="button"
                      onClick={() => applyQuickRange(range)}
                      className={`px-4 py-2 text-sm font-medium border-2 rounded-md transition-colors ${
                        isActive
                          ? 'border-primary bg-primary/10 text-primary hover:bg-primary/20'
                          : 'border-border hover:border-primary hover:bg-primary/5 hover:text-primary'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="border-t mb-6"></div>

            {/* CR-4593: Row 1 - Company Site, Customer Group, Customer Site, Checkboxes */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-4">
              {/* CR-4783: Division filter — label aligned with the dashboard's
                  'Division' filter so the two screens read consistently. */}
              {divisions.length > 0 && (
                <div className="space-y-1.5">
                  <Label htmlFor="division" className="text-xs">Division</Label>
                  <SearchableSelect
                    options={[{ value: 'all', label: 'All Divisions' }, ...divisions.map((d) => ({ value: d.id.toString(), label: d.name }))]}
                    value={localFilters.division}
                    onChange={(val) => updateFilter('division', val || 'all')}
                    placeholder="All Divisions"
                  />
                </div>
              )}

              {/* Company Site (secondary) - when no divisions */}
              {divisions.length === 0 && <div className="space-y-1.5">
                <Label htmlFor="site" className="text-xs">Company Site</Label>
                <SearchableSelect
                  options={[{ value: 'all', label: 'All Company Sites' }, ...sites.map((s) => ({ value: s.id.toString(), label: s.name }))]}
                  value={localFilters.site}
                  onChange={(val) => updateFilter('site', val || 'all')}
                  placeholder="All Company Sites"
                />
              </div>}

              {/* Customer Group */}
              <div className="space-y-1.5">
                <Label htmlFor="customerGroup" className="text-xs">Customer Group</Label>
                <SearchableSelect
                  options={[{ value: 'all', label: 'All Customer Groups' }, ...customerGroups.map((g) => ({ value: g.id.toString(), label: g.name }))]}
                  value={localFilters.customerGroup}
                  onChange={(val) => updateFilter('customerGroup', val || 'all')}
                  placeholder="All Customer Groups"
                />
              </div>

              {/* Customer Site */}
              <div className="space-y-1.5">
                <Label htmlFor="customerSite" className="text-xs">Customer Site</Label>
                <SearchableSelect
                  options={[{ value: 'all', label: 'All Customer Sites' }, ...customerSites.map((s) => ({ value: s.id.toString(), label: `${s.name} (${s.company_name})` }))]}
                  value={localFilters.customerSite}
                  onChange={(val) => updateFilter('customerSite', val || 'all')}
                  placeholder="All Customer Sites"
                />
              </div>

              {/* Checkboxes */}
              <div className="space-y-2 pt-5">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="strategic"
                    checked={localFilters.strategic}
                    onCheckedChange={(checked) => updateFilter('strategic', checked as boolean)}
                  />
                  <Label htmlFor="strategic" className="text-xs cursor-pointer">Strategic</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="hideChildCompanies"
                    checked={localFilters.hideChildCompanies}
                    onCheckedChange={(checked) => updateFilter('hideChildCompanies', checked as boolean)}
                  />
                  <Label htmlFor="hideChildCompanies" className="text-xs cursor-pointer">Hide Child Companies</Label>
                </div>
              </div>

              {/* Warehouse */}
              <div className="space-y-1.5">
                <Label htmlFor="warehouse" className="text-xs">Warehouse</Label>
                <Select
                  value={localFilters.warehouse}
                  onValueChange={(value) => updateFilter('warehouse', value)}
                >
                  <SelectTrigger id="warehouse" className="h-9">
                    <SelectValue placeholder="All Warehouses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Warehouses</SelectItem>
                    {warehouses.map((warehouse) => (
                      <SelectItem key={warehouse.id} value={warehouse.id.toString()}>
                        {warehouse.name} ({warehouse.site_name})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Commodity Group */}
              <div className="space-y-1.5">
                <Label htmlFor="commodityGroup" className="text-xs">
                  Commodity Group
                </Label>
                <Select
                  value={localFilters.commodityGroup}
                  onValueChange={(value) => {
                    updateFilter('commodityGroup', value);
                    // Reset commodity type when group changes
                    updateFilter('commodityType', 'all');
                  }}
                >
                  <SelectTrigger id="commodityGroup" className="h-9">
                    <SelectValue placeholder="All Commodities" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Commodities</SelectItem>
                    {commodityGroups.map((group) => (
                      <SelectItem key={group.id} value={group.id.toString()}>
                        {group.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Commodity Type */}
              <div className="space-y-1.5">
                <Label htmlFor="commodityType" className="text-xs">
                  Commodity Type
                </Label>
                <Select
                  value={localFilters.commodityType}
                  onValueChange={(value) => updateFilter('commodityType', value)}
                  disabled={localFilters.commodityGroup === 'all'}
                >
                  <SelectTrigger id="commodityType" className="h-9">
                    <SelectValue placeholder={localFilters.commodityGroup === 'all' ? 'Select group first' : 'All Types'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {commodityTypes
                      .filter((type) => localFilters.commodityGroup === 'all' || type.commodity_group.toString() === localFilters.commodityGroup)
                      .map((type) => (
                        <SelectItem key={type.id} value={type.id.toString()}>
                          {type.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 2: Date Range, Order Type, Customer Type, Countries, Rating.
                CR-4783: Time Period dropdown removed — replaced by the
                Quick Date Ranges button row above. */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-4">
              {/* Date From */}
              <div className="space-y-1.5">
                <Label htmlFor="dateFrom" className="text-xs">From Date</Label>
                <Input
                  id="dateFrom"
                  type="date"
                  value={localFilters.dateFrom}
                  onChange={(e) => updateFilter('dateFrom', e.target.value)}
                  className="h-9"
                />
              </div>

              {/* Date To */}
              <div className="space-y-1.5">
                <Label htmlFor="dateTo" className="text-xs">To Date</Label>
                <Input
                  id="dateTo"
                  type="date"
                  value={localFilters.dateTo}
                  onChange={(e) => updateFilter('dateTo', e.target.value)}
                  className="h-9"
                />
              </div>

              {/* Order Type Multi-select */}
              <div className="space-y-1.5">
                <Label className="text-xs">Order Type</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full h-9 justify-between font-normal"
                    >
                      <span className="truncate">
                        {localFilters.orderType.length === 0
                          ? 'All Types'
                          : `${localFilters.orderType.length} selected`}
                      </span>
                      <ChevronDown className="h-4 w-4 ml-2 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-56 p-2">
                    <div className="space-y-2">
                      {ORDER_TYPES.map((type) => (
                        <div key={type.value} className="flex items-center gap-2">
                          <Checkbox
                            id={`order-type-${type.value}`}
                            checked={localFilters.orderType.includes(type.value)}
                            onCheckedChange={() => toggleOrderType(type.value)}
                          />
                          <Label
                            htmlFor={`order-type-${type.value}`}
                            className="text-sm cursor-pointer"
                          >
                            {type.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Customer Type */}
              <div className="space-y-1.5">
                <Label htmlFor="customerType" className="text-xs">Customer Type</Label>
                <Select
                  value={localFilters.customerType}
                  onValueChange={(value) => updateFilter('customerType', value)}
                >
                  <SelectTrigger id="customerType" className="h-9">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    {CUSTOMER_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* CR-4783: Company Country — derived from accessible sites */}
              <div className="space-y-1.5">
                <Label htmlFor="siteCountry" className="text-xs">Company Country</Label>
                <SearchableSelect
                  options={[
                    { value: 'all', label: 'All Countries' },
                    ...Array.from(new Set(sites.map((s) => s.country).filter(Boolean)))
                      .sort()
                      .map((c) => ({ value: c, label: c })),
                  ]}
                  value={localFilters.siteCountry}
                  onChange={(val) => updateFilter('siteCountry', val || 'all')}
                  placeholder="All Countries"
                />
              </div>

              {/* CR-4783: Customer Country — derived from accessible customer sites */}
              <div className="space-y-1.5">
                <Label htmlFor="customerCountry" className="text-xs">Customer Country</Label>
                <SearchableSelect
                  options={[
                    { value: 'all', label: 'All Countries' },
                    ...Array.from(new Set(customerSites.map((s) => s.country).filter(Boolean)))
                      .sort()
                      .map((c) => ({ value: c, label: c })),
                  ]}
                  value={localFilters.customerCountry}
                  onChange={(val) => updateFilter('customerCountry', val || 'all')}
                  placeholder="All Countries"
                />
              </div>

              {/* Rating */}
              <div className="space-y-1.5">
                <Label htmlFor="rating" className="text-xs">Rating</Label>
                <Select
                  value={localFilters.rating}
                  onValueChange={(value) => {
                    if (value === 'N/A') {
                      setLocalFilters(prev => ({ ...prev, rating: value, excludeNARatings: false }));
                    } else {
                      updateFilter('rating', value);
                    }
                  }}
                >
                  <SelectTrigger id="rating" className="h-9">
                    <SelectValue placeholder="All Ratings" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Ratings</SelectItem>
                    {ratingThresholds.length > 0 ? (
                      ratingThresholds.map((threshold) => (
                        <SelectItem key={threshold.id} value={threshold.grade}>
                          {threshold.display_label || threshold.label || threshold.grade}
                        </SelectItem>
                      ))
                    ) : (
                      <>
                        <SelectItem value="A">A (90-100%)</SelectItem>
                        <SelectItem value="B">B (75-90%)</SelectItem>
                        <SelectItem value="C">C (60-75%)</SelectItem>
                        <SelectItem value="D">D (40-60%)</SelectItem>
                        <SelectItem value="F">F (0-40%)</SelectItem>
                      </>
                    )}
                    <SelectItem value="N/A">N/A (No Data)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 3: Additional options and action buttons */}
            <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
              <div className="flex items-center gap-6">
                {/* Exclude N/A Ratings */}
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="excludeNARatings"
                    checked={localFilters.excludeNARatings}
                    onCheckedChange={(checked) => updateFilter('excludeNARatings', checked as boolean)}
                  />
                  <Label htmlFor="excludeNARatings" className="text-sm cursor-pointer">
                    Exclude N/A Ratings
                  </Label>
                </div>

                {/* Data Mode Toggle */}
                <div className="flex items-center gap-2 border rounded-md p-1">
                  <Button
                    variant={localFilters.dataMode === 'historical' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => updateFilter('dataMode', 'historical')}
                  >
                    Historical
                  </Button>
                  <Button
                    variant={localFilters.dataMode === 'live' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => updateFilter('dataMode', 'live')}
                  >
                    Live
                  </Button>
                </div>
              </div>

              {/* Apply Filters Button */}
              <Button
                size="sm"
                onClick={() => onApplyFilters(localFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                Apply Filters
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
